import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  LinearProgress,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { useGamificationService } from '../services/gamificationService';
import GameMasterPanel from '../components/GameMasterPanel';
import QuestPanel from '../components/QuestPanel';
import Leaderboard from '../components/Leaderboard';
import FortressMap from '../components/FortressMap';
import ActivityFeed from '../components/ActivityFeed';
import InlineAlert from '../../../shared/components/InlineAlert';
import type { PlayerProfile } from '../types';

function XPBar({ xp, level }: { xp: number; level: number }) {
  const xpPerLevel = 1000;
  const xpInLevel = xp % xpPerLevel;
  const pct = (xpInLevel / xpPerLevel) * 100;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Level {level}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {xpInLevel} / {xpPerLevel} XP
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{ height: (theme) => theme.spacing(1), borderRadius: 1 }} />
    </Box>
  );
}

const PlayerProfilePage: React.FC = () => {
  const service = useGamificationService();
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    service.getPlayerProfile()
      .then(setProfile)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load profile'))
      .finally(() => setLoading(false));
  }, [service]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const profileValid = profile && typeof profile.xp === 'number';

  if (error || !profileValid) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <InlineAlert severity="info" sx={{ mb: 3 }}>
          {error ?? 'Game profile not initialized yet. Complete your first quest to get started!'}
        </InlineAlert>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '2 1 auto', minWidth: (theme) => theme.spacing(40) }}>
            <GameMasterPanel />
          </Box>
        </Box>
      </Container>
    );
  }

  const masteryEntries = Object.entries(profile.mastery_tracks ?? {}).sort(([, a], [, b]) => b - a);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: (theme) => theme.spacing(7),
              height: (theme) => theme.spacing(7),
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StarIcon sx={{ color: 'primary.contrastText' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{profile.display_name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {profile.xp.toLocaleString()} XP total
            </Typography>
          </Box>
          {profile.active_streak_days > 0 && (
            <Chip
              icon={<LocalFireDepartmentIcon />}
              label={`${profile.active_streak_days}-day streak`}
              color="warning"
            />
          )}
        </Box>
        <XPBar xp={profile.xp} level={profile.level} />
      </Paper>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ flex: '2 1 auto', minWidth: (theme) => theme.spacing(40) }}>
          <GameMasterPanel />
          <Box sx={{ mt: 3 }}>
            <QuestPanel />
          </Box>
        </Box>

        <Box sx={{ flex: '1 1 auto', minWidth: (theme) => theme.spacing(30), display: 'flex', flexDirection: 'column', gap: 3 }}>
          {masteryEntries.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Mastery Tracks
              </Typography>
              <List disablePadding dense>
                {masteryEntries.map(([domain, score]) => (
                  <ListItem key={domain} disablePadding sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <ListItemText
                        primary={domain}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {score}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, score)}
                      sx={{ width: '100%', height: (theme) => theme.spacing(0.5), borderRadius: 1 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Stats
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Level</Typography>
                <Typography variant="body2">{profile.level}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Total XP</Typography>
                <Typography variant="body2">{profile.xp.toLocaleString()}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Current streak</Typography>
                <Typography variant="body2">{profile.active_streak_days} days</Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 auto', minWidth: (theme) => theme.spacing(40) }}>
          <FortressMap />
        </Box>
        <Box sx={{ flex: '1 1 auto', minWidth: (theme) => theme.spacing(30) }}>
          <Leaderboard />
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        <ActivityFeed />
      </Box>
    </Container>
  );
};

export default PlayerProfilePage;
